import { isPlatformBrowser } from '@angular/common';
import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { LogService } from 'shared';

import type { TokenResponseDto } from 'api-client';

export interface TokenPayload {
  exp?: number;
  iat?: number;
  sub?: string;
  [key: string]: unknown;
}

@Injectable({
  providedIn: 'root',
})
export class TokenStorageService {
  private readonly DEVICE_TOKEN_KEY = 'device_token';
  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));
  private readonly logger = inject(LogService);

  constructor() {
    this.clearToken();
  }

  getToken(): TokenResponseDto | null {
    if (!this.isBrowser) {
      return null;
    }

    return null;
  }

  clearToken(): void {
    if (this.isBrowser) {
      localStorage.removeItem('access_token');
      localStorage.removeItem('token_metadata');
      localStorage.removeItem('refresh_token');
      sessionStorage.removeItem('access_token');
      sessionStorage.removeItem('token_metadata');
      sessionStorage.removeItem('refresh_token');
    }
  }

  isRememberMeEnabled(): boolean {
    if (!this.isBrowser) {
      return false;
    }
    return false;
  }

  saveDeviceToken(deviceId: string, token: string, expiresAt: Date): void {
    if (this.isBrowser) {
      localStorage.setItem(
        this.DEVICE_TOKEN_KEY,
        JSON.stringify({
          deviceId,
          token,
          expiresAt: expiresAt.toISOString(),
        }),
      );
    }
  }

  getDeviceToken(): { deviceId: string; token: string } | null {
    if (!this.isBrowser) return null;

    const deviceTokenStr = localStorage.getItem(this.DEVICE_TOKEN_KEY);
    if (!deviceTokenStr) return null;

    try {
      const deviceToken = JSON.parse(deviceTokenStr) as unknown;

      if (!this.isValidDeviceToken(deviceToken)) {
        this.clearDeviceToken();
        return null;
      }

      if (new Date(deviceToken.expiresAt) < new Date()) {
        this.clearDeviceToken();
        return null;
      }

      return {
        deviceId: deviceToken.deviceId,
        token: deviceToken.token,
      };
    } catch {
      this.clearDeviceToken();
      return null;
    }
  }

  private isValidDeviceToken(
    data: unknown,
  ): data is { deviceId: string; token: string; expiresAt: string } {
    return (
      !!data &&
      typeof data === 'object' &&
      'deviceId' in data &&
      'token' in data &&
      'expiresAt' in data &&
      typeof (data as Record<string, unknown>)['deviceId'] === 'string' &&
      typeof (data as Record<string, unknown>)['token'] === 'string' &&
      typeof (data as Record<string, unknown>)['expiresAt'] === 'string'
    );
  }

  clearDeviceToken(): void {
    if (this.isBrowser) {
      localStorage.removeItem(this.DEVICE_TOKEN_KEY);
    }
  }

  public decodeToken(token: string): TokenPayload | null {
    if (!this.isBrowser) {
      return null;
    }

    try {
      const base64Url = token.split('.')[1];
      if (!base64Url) {
        return null;
      }
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        window
          .atob(base64)
          .split('')
          .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join(''),
      );
      return JSON.parse(jsonPayload) as TokenPayload;
    } catch {
      return null;
    }
  }

  public isAccessTokenExpired(): boolean {
    const token = this.getToken();
    if (!token?.accessToken) return true;
    const payload = this.decodeToken(token.accessToken);
    if (!payload?.exp) return true;
    return Date.now() >= payload.exp * 1000;
  }
}
